import { StandardEvents } from '@shopify/events';

/** Enhances the native Horizon form; Shopify remains responsible for cart writes. */
class PIBuyingSection extends HTMLElement {
  #abort;
  #root;
  #section;
  #chosen = false;
  #pending = false;
  #failed = false;
  #generation = 0;
  #variant;
  #price = '';
  #canAdd = false;
  #requiresSize = false;

  async connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.#root = this.closest('.pi-buying-details');
    this.#section = this.closest('.shopify-section');
    if (!this.#root || !this.#section) return;
    const definitions = [customElements.whenDefined('product-form-component')];
    if (this.#root.querySelector('variant-picker')) definitions.push(customElements.whenDefined('variant-picker'));
    await Promise.all(definitions);
    if (signal.aborted) return;

    this.#section.classList.add('pi-buying-section');
    this.#requiresSize = this.#root.querySelectorAll('[data-pi-option="size"] input').length > 1;
    this.#chosen = !this.#requiresSize || this.dataset.explicitVariant === 'true';
    this.#pending = false;
    this.#failed = false;
    this.#price = this.dataset.price || '';
    this.#canAdd = this.#root.querySelector('product-form-component [name="add"]')?.disabled === false;
    const json = this.querySelector('[data-pi-variant]');
    try { this.#variant = JSON.parse(json?.textContent || 'null'); } catch { this.#variant = null; }

    this.#root.addEventListener('change', this.#onChange, { capture: true, signal });
    this.#section.addEventListener('click', this.#guardPurchase, { capture: true, signal });
    this.#root.addEventListener('submit', this.#guardPurchase, { capture: true, signal });
    this.#section.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    this.#render();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#generation++;
    this.#section?.classList.remove('pi-buying-section');
  }

  #onChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (event.target.matches('[data-pi-option="size"] input')) this.#chosen = true;
  };

  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || !this.#root.contains(event.target)) return;
    const generation = ++this.#generation;
    this.#pending = true;
    this.#failed = false;
    this.#render();
    event.promise.then(({ detail }) => {
      // Native listeners first update the form, price, media, and sticky button.
      queueMicrotask(() => {
        if (this.#abort.signal.aborted || generation !== this.#generation) return;
        this.#variant = detail?.resource ?? null;
        const nextController = detail?.html?.querySelector(`pi-buying-section[data-product-id="${this.dataset.productId}"]`);
        this.#price = nextController?.dataset.price || '';
        const nextButton = nextController?.closest('.pi-buying-details')?.querySelector('product-form-component [name="add"]');
        this.#canAdd = nextButton?.disabled === false;
        this.#pending = false;
        this.#render();
      });
    }).catch((error) => {
      if (this.#abort.signal.aborted || generation !== this.#generation || error?.name === 'AbortError') return;
      this.#pending = false;
      this.#failed = true;
      this.#render();
    });
  };

  #guardPurchase = (event) => {
    if (!(event.target instanceof Element)) return;
    const isSubmit = event.type === 'submit' && event.target.matches('product-form-component form');
    const isButton = event.target.closest('product-form-component [name="add"], .sticky-add-to-cart__button');
    if (!isSubmit && !isButton) return;
    if (this.#chosen && !this.#pending && !this.#failed) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.#chosen) return;
    const sizes = this.#root.querySelector('[data-pi-option="size"]');
    sizes?.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    sizes?.querySelector('input:not([aria-disabled="true"])')?.focus({ preventScroll: true });
    const selection = this.#root.querySelector('[data-pi-selection]');
    if (selection) selection.textContent = 'Please choose her size above.';
  };

  #render() {
    this.#root.dataset.piSizeChosen = String(this.#chosen);
    this.#root.dataset.piPending = String(this.#pending || this.#failed);
    this.#root.setAttribute('aria-busy', String(this.#pending));
    if (!this.#chosen) {
      this.#root.querySelectorAll('[data-pi-option="size"] input').forEach((input) => {
        input.checked = false;
        input.dataset.currentChecked = 'false';
        input.dataset.previousChecked = 'false';
      });
    }

    const bundle = this.#root.querySelector('[data-pi-option="bundle"] input:checked');
    const size = this.#root.querySelector('[data-pi-option="size"] input:checked');
    const available = this.#variant?.available === true && this.#canAdd;
    let text;
    if (this.#pending) text = 'Updating…';
    else if (this.#failed) text = 'Selection unavailable';
    else if (!this.#chosen) text = 'Choose a size';
    else if (!this.#variant) text = this.dataset.unavailableLabel;
    else if (!available) text = this.dataset.soldOutLabel;
    else text = this.dataset.addLabel + (this.#price ? ` · ${this.#price}` : '');

    const button = this.#root.querySelector('product-form-component [name="add"]');
    if (button) {
      button.disabled = this.#pending || this.#failed || (this.#chosen && !available);
      const label = button.querySelector('[data-pi-button-text]');
      if (label) label.textContent = text;
    }
    const sticky = this.#section.querySelector('.sticky-add-to-cart__button');
    if (sticky) {
      sticky.disabled = this.#pending || this.#failed || (this.#chosen && !available);
      sticky.setAttribute('aria-label', text);
      const label = sticky.querySelector('.add-to-cart-text__content > span');
      if (label) label.textContent = this.#chosen && !this.#pending && available ? this.dataset.addLabel : text;
    }

    const summary = this.#root.querySelector('[data-pi-selection]');
    if (summary) {
      summary.textContent = this.#failed
        ? 'We couldn’t update this selection. Please select a size or bundle again.'
        : this.#pending ? 'Updating your selection…'
        : this.#chosen ? [size ? `Size ${size.value}` : '', bundle?.value || ''].filter(Boolean).join(' · ')
        : 'Select a size to continue.';
    }
  }
}

if (!customElements.get('pi-buying-section')) customElements.define('pi-buying-section', PIBuyingSection);
